(() => {
  const DAY_MS = 24 * 60 * 60 * 1000;
  const DEFAULT_TIME_ZONE = 'America/New_York';
  const formatterCache = new Map();

  const parseDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const timestamp = Date.UTC(year, month - 1, day);
    const check = new Date(timestamp);
    if (check.getUTCFullYear() !== year || check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) return null;
    return { year, month, day };
  };

  const formatterFor = (timeZone) => {
    const zone = timeZone || DEFAULT_TIME_ZONE;
    if (!formatterCache.has(zone)) {
      formatterCache.set(zone, new Intl.DateTimeFormat('en-US', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23'
      }));
    }
    return formatterCache.get(zone);
  };

  const zonedParts = (instant, timeZone) => {
    let formatter;
    try {
      formatter = formatterFor(timeZone);
    } catch (_error) {
      formatter = formatterFor(DEFAULT_TIME_ZONE);
    }
    const parts = formatter.formatToParts(instant);
    const values = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, Number(part.value)]));
    return { year: values.year, month: values.month, day: values.day, hour: values.hour, minute: values.minute, second: values.second };
  };

  const calendarDaysUntil = (targetDate, instant = new Date(), timeZone = DEFAULT_TIME_ZONE) => {
    const target = parseDate(targetDate);
    if (!target || Number.isNaN(instant.getTime())) return null;
    const today = zonedParts(instant, timeZone);
    return Math.round((Date.UTC(target.year, target.month - 1, target.day) - Date.UTC(today.year, today.month - 1, today.day)) / DAY_MS);
  };

  const stateForInstant = (targetDate, instant = new Date(), timeZone = DEFAULT_TIME_ZONE) => {
    const days = calendarDaysUntil(targetDate, instant, timeZone);
    if (days === null) return { phase: 'invalid', days: null, visible: false };
    if (days < 0) return { phase: 'complete', days, visible: false };
    if (days === 0) return { phase: 'halloween', days, visible: true };
    return { phase: 'countdown', days, visible: true };
  };

  const zonedMidnightEpoch = (date, timeZone) => {
    const expected = Date.UTC(date.year, date.month - 1, date.day);
    let guess = expected;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      const observed = zonedParts(new Date(guess), timeZone);
      guess += expected - Date.UTC(observed.year, observed.month - 1, observed.day, observed.hour, observed.minute, observed.second);
    }
    return guess;
  };

  const millisecondsUntilNextMidnight = (instant = new Date(), timeZone = DEFAULT_TIME_ZONE) => {
    const current = zonedParts(instant, timeZone);
    const next = new Date(Date.UTC(current.year, current.month - 1, current.day + 1));
    const nextDate = { year: next.getUTCFullYear(), month: next.getUTCMonth() + 1, day: next.getUTCDate() };
    return Math.max(0, zonedMidnightEpoch(nextDate, timeZone) - instant.getTime());
  };

  const secondsUntilNextMidnight = (instant = new Date(), timeZone = DEFAULT_TIME_ZONE) => {
    return Math.ceil(millisecondsUntilNextMidnight(instant, timeZone) / 1000);
  };

  const formatCountdown = (totalSeconds) => {
    const seconds = Math.max(0, Number(totalSeconds) || 0);
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return [hours, minutes, remainingSeconds].map((value) => String(value).padStart(2, '0')).join(':');
  };

  const update = (element, instant = new Date()) => {
    const state = stateForInstant(element.dataset.targetDate, instant, element.dataset.timeZone);
    const fallback = element.querySelector('[data-ptt-halloween-fallback]');
    const countdown = element.querySelector('[data-ptt-halloween-countdown-content]');
    const greeting = element.querySelector('[data-ptt-halloween-greeting]');
    const timer = element.querySelector('[data-ptt-halloween-time]');
    element.hidden = !state.visible;
    element.dataset.pttHalloweenState = state.phase;
    if (fallback) fallback.hidden = true;
    const showTimer = state.phase === 'countdown' || state.phase === 'halloween';
    if (countdown) countdown.hidden = !showTimer;
    if (greeting) greeting.hidden = state.phase !== 'halloween';
    if (showTimer) {
      state.seconds = secondsUntilNextMidnight(instant, element.dataset.timeZone);
      if (timer) {
        const formatted = formatCountdown(state.seconds);
        timer.textContent = formatted;
        if (typeof timer.setAttribute === 'function') timer.setAttribute('datetime', `PT${state.seconds}S`);
      }
    } else {
      state.seconds = null;
    }
    return state;
  };

  const schedule = (element) => {
    let timerId = null;
    const refresh = () => {
      if (timerId !== null && typeof window !== 'undefined' && typeof window.clearTimeout === 'function') window.clearTimeout(timerId);
      timerId = null;
      const state = update(element);
      if (!state.visible) return;
      const delay = Math.max(25, 1000 - (Date.now() % 1000) + 5);
      if (typeof window !== 'undefined' && typeof window.setTimeout === 'function') timerId = window.setTimeout(refresh, delay);
    };
    const refreshWhenVisible = () => {
      if (typeof document === 'undefined' || !document.hidden) refresh();
    };
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', refreshWhenVisible);
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') window.addEventListener('focus', refresh);
    refresh();
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-ptt-halloween-countdown]').forEach((element) => {
      if (element.dataset.pttHalloweenInitialized === 'true') return;
      element.dataset.pttHalloweenInitialized = 'true';
      schedule(element);
    });
  };

  const api = Object.freeze({ calendarDaysUntil, stateForInstant, millisecondsUntilNextMidnight, secondsUntilNextMidnight, formatCountdown, initialize, update });
  globalThis.PTTHalloweenCountdown = api;
  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
    else initialize();
  }
})();
